import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { Trash2 } from 'lucide-react';
import { Team } from 'modules/teams/domain/interfaces/team';
import { BorderedSelect } from 'modules/teams/ui/components/styles';
import { TeamGroupStatisticsRow } from 'modules/championship/domain/interfaces/team-group-statistics';
import React, { useMemo } from 'react';
import { SelectOption } from 'ud-form/components/select/component';
import TeamLogo from 'ud-ui/components/team-logo';
import UDTippy from 'ud-ui/components/tippy';
import { theme } from 'styles/theme/theme';

export const TableCell = styled.div`
  border: 1px solid ${theme.colors.SurfacePlatinum20};
  background: ${theme.colors.SurfaceWhite};
  padding: 10px;

  &:first-child {
    padding-left: 14px;
  }
`;

export const TableHeader = styled.div`
  border-bottom: 1px solid ${theme.colors.SurfaceIndependence40};
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 17px;
  line-height: 22px;
`;

const TableRowWrapper = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 17px;
  line-height: 22px;
`;

const TeamInfoWrapper = styled.div`
  display: flex;
  align-items: center;

  .team-select-logo {
    margin-right: 8px;
  }
`;

interface TeamInfoProps { team: Team }

const TeamInfo = ({ team }: TeamInfoProps) => {
  return (
    <TeamInfoWrapper>
      <TeamLogo className="team-select-logo" url={team.logo?.url} size={32} />
      {team.name}
    </TeamInfoWrapper>
  );
};

const teamToTeamOption = (team: Team): SelectOption<number, Team> => ({
  label: <TeamInfo team={team} />,
  value: team.id,
});

type TeamWithStatistics = {
  team: Team | null;
  originalIndex: number;
  statistics: TeamGroupStatisticsRow | null;
};

interface TableProps {
  className?: string;
  isEditMode?: boolean;
  teams: (Team | null)[];
  stageTeams?: Team[];
  statistics: TeamGroupStatisticsRow[];
  externallySelectedTeamIds?: Set<number>;
  removeTeamClick?: (index: number) => void;
  teamChange?: (index: number, teamId: number) => void;
}

export const Table = (props: TableProps) => {
  const {
    className,
    isEditMode = false,
    teams,
    stageTeams = [],
    statistics,
    externallySelectedTeamIds = new Set(),
    removeTeamClick = () => {},
    teamChange = () => {},
  } = props;
  const { t } = useLingui();

  const statisticsByTeam: Record<number, TeamGroupStatisticsRow> = useMemo(() => {
    return statistics.reduce((result, item) => {
      return {
        ...result,
        [item.teamId]: item,
      };
    }, {});
  }, [statistics]);

  const teamsWithStatistics: TeamWithStatistics[] = useMemo(() => {
    return teams
      .map((team, originalIndex) => ({
        team,
        originalIndex,
        statistics: team ? statisticsByTeam[team.id] : null,
      }))
      .sort((a, b) => {
        if (a.team === null) return 1;
        if (b.team === null) return -1;
        return (b.statistics?.score || 0) - (a.statistics?.score || 0);
      });
  }, [teams, statisticsByTeam]);

  const selectedTeamIds = useMemo(() => {
    return new Set(teams.filter(Boolean).map((t) => t!.id));
  }, [teams]);

  return (
    <div className={className}>
      <TableHeader className="flex flex-row">
        <UDTippy content={t`Номер`} placement="top">
          <TableCell className="w-1/12">#</TableCell>
        </UDTippy>
        <TableCell className="w-1/4">
          <Trans>Команды</Trans>
        </TableCell>
        <UDTippy content={t`Игр сыграно`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'И', context: 'standings: played' })}</TableCell>
        </UDTippy>
        <UDTippy content={t`Игр выиграно`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'В', context: 'standings: won' })}</TableCell>
        </UDTippy>
        <UDTippy content={t`Игр в ничью`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'Н', context: 'standings: drawn' })}</TableCell>
        </UDTippy>
        <UDTippy content={t`Игр проиграно`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'П', context: 'standings: lost' })}</TableCell>
        </UDTippy>
        <UDTippy content={t`Голов забито`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'ГЗ', context: 'standings: goals for' })}</TableCell>
        </UDTippy>
        <UDTippy content={t`Голов пропущено`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'ГП', context: 'standings: goals against' })}</TableCell>
        </UDTippy>
        <UDTippy content={t`Разница мечей`} placement="top">
          <TableCell className="flex w-1/12 justify-center">+/-</TableCell>
        </UDTippy>
        <UDTippy content={t`Кол-во очков за этап`} placement="top">
          <TableCell className="flex w-1/12 justify-center">{t({ message: 'О', context: 'standings: points' })}</TableCell>
        </UDTippy>
      </TableHeader>

      {teams.length > 0 && (
        <>
          {teamsWithStatistics.map(({ team, originalIndex, statistics }, displayIndex) => {
            const teamOptions = stageTeams
              .filter((t) => !externallySelectedTeamIds.has(t.id) && (!selectedTeamIds.has(t.id) || t.id === team?.id))
              .map(teamToTeamOption);

            return (
              <TableRow
                key={`${originalIndex}_${team?.id}`}
                displayIndex={displayIndex}
                originalIndex={originalIndex}
                isEditMode={isEditMode}
                team={team}
                teamStatistics={statistics}
                teamOptions={teamOptions}
                removeTeamClick={removeTeamClick}
                teamChange={teamChange}
              />
            );
          })}
        </>
      )}
    </div>
  );
};

interface TeamCellBodyProps {
  isEditMode: boolean;
  teamOptions: SelectOption<number, Team>[];
  team?: Team | null;
  handleTeamChange: (option: any) => void;
}

const TeamCellBody = (props: TeamCellBodyProps) => {
  const { isEditMode, teamOptions, team, handleTeamChange } = props;
  const { t } = useLingui();

  if (isEditMode) {
    const value = team ? teamToTeamOption(team) : undefined;
    return (
      <BorderedSelect
        className="flex-1"
        name="team"
        value={value}
        options={teamOptions}
        onChange={handleTeamChange}
        divProps={{ className: 'flex-1' }}
        placeholder={t`Добавить команду`}
      />
    );
  }

  if (team) {
    return <TeamInfo team={team} />;
  }

  return null;
};

interface TableRowProps {
  displayIndex: number;
  originalIndex: number;
  isEditMode?: boolean;
  team?: Team | null;
  teamStatistics: TeamGroupStatisticsRow | null;
  teamOptions: SelectOption<number, Team>[];
  removeTeamClick?: (index: number) => void;
  teamChange?: (index: number, teamId: number) => void;
}

export const TableRow = (props: TableRowProps) => {
  const {
    isEditMode = false,
    displayIndex,
    originalIndex,
    team,
    teamStatistics,
    teamOptions,
    removeTeamClick = () => {},
    teamChange = () => {},
  } = props;

  const { t } = useLingui();
  const handleRemoveClick = () => removeTeamClick(originalIndex);
  const handleTeamChange = (option: any) => teamChange(originalIndex, option.value);

  return (
    <TableRowWrapper className="flex flex-row">
      <TableCell className="flex w-1/12 items-center justify-center">{team !== null ? displayIndex + 1 : '--'}</TableCell>
      <TableCell className="flex w-1/4 items-center gap-2">
        {isEditMode && team !== null && (
          <UDTippy content={t`Удалить`} placement={'top'}>
            <span className="flex-shrink-0 cursor-pointer" onClick={handleRemoveClick}>
              <Trash2 size={16} color="#FF5630" />
            </span>
          </UDTippy>
        )}
        <TeamCellBody isEditMode={isEditMode} team={team} teamOptions={teamOptions} handleTeamChange={handleTeamChange} />
      </TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.gamesPlayed ?? '--'}</TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.gamesWon ?? '--'}</TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.gamesDraw ?? '--'}</TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.gamesLost ?? '--'}</TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.goalsWon ?? '--'}</TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.goalsLost ?? '--'}</TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">
        {teamStatistics ? teamStatistics.goalsWon - teamStatistics.goalsLost : '--'}
      </TableCell>
      <TableCell className="flex w-1/12 items-center justify-center">{teamStatistics?.score ?? '--'}</TableCell>
    </TableRowWrapper>
  );
};
